<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n();
useHead({
  titleTemplate: () => t("seo.layouts./viber-chatbots.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./viber-chatbots.description") },
    {
      property: "og:title", content: () => t("seo.layouts./viber-chatbots.title")
    },
    { property: "og:description", content: () => t("seo.layouts./viber-chatbots.description") },
    {
      property: "og:image",
      content:
        "/images/chatbot.png",
    },
    { property: "og:image:width", content: "2668" },
    { property: "og:image:height", content: "2000" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@smsbatcom" },
  ],
});
const popularQuestions = ref([
  {
    question: t('chatbots.questions[0].question'),
    answer: t('chatbots.questions[0].answer'),
    isOpen: false
  },
  {
    question: t('chatbots.questions[1].question'),
    answer: t('chatbots.questions[1].answer'),
    isOpen: false
  },
  {
    question: t('chatbots.questions[2].question'),
    answer: t('chatbots.questions[2].answer'),
    isOpen: false
  },
  {
    question: t('chatbots.questions[3].question'),
    answer: t('chatbots.questions[3].answer'),
    isOpen: false
  },
  {
    question: t('chatbots.questions[4].question'),
    answer: t('chatbots.questions[4].answer'),
    isOpen: false
  },
])
onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})
</script>

<template>
  <div>

    <div class="main-banner">
      <div class="main-banner-content !justify-center gap-8">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('chatbots.title') }}
          </div>
          <div class="banner-description">
            {{ $t('chatbots.subtitle') }}
          </div>
          <div class="banner-action">
            <NuxtLink to="/pricing/viber-chatbots/" class="nav-link button filled whitespace-nowrap !text-sm">
              <p>{{ $t('actions.tryForFree') }}</p>
            </NuxtLink>
            <NuxtLink to="viber://chat?service=26076" class="button outlined" external>{{ $t('actions.contactUs') }}
            </NuxtLink>
          </div>
        </div>
        <div class="banner-image-container">
          <NuxtImg preload sizes="xs:100vw sm:100vw md:50vw lg:600px xl:800px xxl:1000px" src="/images/chatbot.webp" width="800" height="600" alt="Viber Chatbot Integration" format="webp" loading="eager" fetchpriority="high" class="w-full h-auto object-contain"
            :title="`${$t('seo.layouts./viber-chatbots.description')}, ${$t('global.image')} 13`" />
        </div>
      </div>
    </div>

    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('chatbots.points.title') }}</div>
        </div>
        <div class="section-content">
          <div class="about-section flex flex-col gap-16">
            <div class="about-item">
              <div class="about-item-text mx-auto mx-auto flex flex-col">
                <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('chatbots.points.elems[0].title') }}</span>
                {{ $t('chatbots.points.elems[0].text') }}
              </div>
              <NuxtImg src="/images/chatbot/bot-banner1.webp" width="462" height="300" format="webp" loading="lazy" class="w-full h-auto object-contain"
                alt="Chatbot feature illustration"
                :title="`${$t('seo.layouts./viber-chatbots.description')}, ${$t('global.image')} 14`" />
            </div>
            <div class="about-item">
              <NuxtImg src="/images/chatbot/bot-banner2.webp" width="462" height="300" format="webp" loading="lazy" class="w-full h-auto object-contain"
                alt="Chatbot communication illustration"
                :title="`${$t('seo.layouts./viber-chatbots.description')}, ${$t('global.image')} 15`" />
              <div class="about-item-text mx-auto flex flex-col">
                <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('chatbots.points.elems[1].title') }}</span>
                {{ $t('chatbots.points.elems[1].text') }}
              </div>
            </div>
            <div class="about-item">
              <div class="about-item-text mx-auto flex flex-col">
                <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('chatbots.points.elems[2].title') }}</span>
                {{ $t('chatbots.points.elems[2].text') }}
              </div>
              <NuxtImg src="/images/chatbot/bot-banner3.webp" width="462" height="300" format="webp" loading="lazy" class="w-full h-auto object-contain"
                alt="Chatbot integration details"
                :title="`${$t('seo.layouts./viber-chatbots.description')}, ${$t('global.image')} 16`" />
            </div>
            <div class="about-item">
              <NuxtImg src="/images/chatbot/bot-banner4.webp" width="200" height="130" format="webp" loading="lazy" class="w-full h-auto object-contain"
                alt="Chatbot automation graphic"
                :title="`${$t('seo.layouts./viber-chatbots.description')}, ${$t('global.image')} 17`" />
              <div class="about-item-text mx-auto flex flex-col">
                <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('chatbots.points.elems[3].title') }}</span>
                {{ $t('chatbots.points.elems[3].text') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title after:opacity-0">{{ $t('chatbots.communication.title') }}</div>
          {{ $t('chatbots.communication.subtitle') }}
        </div>
        <div class="section-content">
          <div class="about-section flex flex-row gap-16">
            <div class="service-hint flex flex-[50%] flex-col">
              <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('chatbots.communication.conversations.title')
              }}</span>
              {{ $t('chatbots.communication.conversations.subtitle') }}
            </div>
            <div class="service-hint flex flex-[50%] flex-col">
              <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('chatbots.communication.answers.title')
              }}</span>
              {{ $t('chatbots.communication.answers.subtitle') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section service-domain" id="rates">
      <div class="container">
        <div class="service-price">
          <div class="price-label">{{ $t('global.price') }}</div>
        </div>
        <div class="section-head text-center">
          <div class="section-head-title after:opacity-0">{{ $t('chatbots.ratesTitle') }}</div>
        </div>
      </div>
    </div>

    <div class="mb-10">
      <LazyBotsDefaultRatesTable />
    </div>

    <div class="page-section">
      <div class="container">
        <div class="service-hint mb-8">
          <p>
            {{ $t('chatbots.messages.subtitle[0]') }} <span class="highlight-blue">{{
              $t('chatbots.messages.subtitle[1]') }}</span>
            {{ $t('chatbots.messages.subtitle[2]') }}</p>
        </div>
        <div class="section-head text-center">
          <div class="section-head-title after:opacity-0">{{ $t('chatbots.messages.title') }}</div>
        </div>
        <div class="section-content">
          <div class="about-section flex flex-row gap-16">
            <div class="service-hint flex flex-[50%] flex-col">
              <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('chatbots.messages.sessionMessage.title')
              }}</span>
              {{ $t('chatbots.messages.sessionMessage.subtitle') }}
            </div>
            <div class="service-hint flex flex-[50%] flex-col">
              <span class="highlight-blue mb-2 font-bold text-xl">{{ $t('chatbots.messages.initMessages.title')
              }}</span>
              {{ $t('chatbots.messages.initMessages.subtitle') }}
            </div>
          </div>
          <div class="text-center mt-8">
            <NuxtLink :to="localePath('/register-bot')" class="nav-link">
              <p class="nav-link button filled w-1/3 mx-auto">{{ $t('links.registerBot') }}</p>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section">
      <div class="container">
        <ClientOnly>
          <PopularQuestions :title="$t('chats.questionsTitle')" :questions="popularQuestions" />
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.about-item img {
  width: 100%;
}

.banner-image-container {
  width: 100%;
  margin-top: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 800px;
}

@media (max-width: 900px) {
  .banner-image-container {
    margin-top: 50px;
  }
}

@media (max-width: 850px) {
  .main-banner-content {
    display: block;
  }
}

.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}
</style>
